import React from 'react'
import styled, { useTheme } from 'styled-components'
import type { Theme, ThemeColorName } from '../themeProvider/theme'

/**
 * Props for the `OutlineBadge` component.
 *
 * Extends the regular span HTML attributes to allow seamless integration
 * wherever an inline label/badge is needed.
 */
type OutlineBadgeProps = React.HTMLAttributes<HTMLSpanElement> & {
	/**
	 * Badge color.
	 *
	 * You can pass either a theme color name (e.g., `primary`, `error`) that exists in the theme
	 * or any valid CSS color value (hex, rgb(a), hsl(a), color keyword, etc.).
	 */
	color?: ThemeColorName | string
	/**
	 * Reduces the vertical padding to create a more compact badge.
	 *
	 * When `true`, padding becomes `4px 10px`; otherwise it defaults to `8px 10px`.
	 */
	small?: boolean
	/**
	 * The content to render inside the badge, typically a short label.
	 */
	children: React.ReactNode
}

/**
 * Visual implementation for the outlined badge.
 *
 * Note the transient props `$color` and `$small` (prefixed with `$`) are used to
 * keep them from being forwarded to the underlying DOM element by styled-components.
 */
const Badge = styled.span<{ $color: string; $small?: boolean }>`
	display: inline-block;
	color: ${p => p.$color};
	background-color: #fff;
	border: 1px solid ${p => p.$color};
	padding: ${p => (p.$small ? '4px 10px' : '8px 10px')};
	border-radius: 100px;
	font-weight: bold;
`

/**
 * OutlineBadge
 *
 * Small inline badge with a white background and colored outline/text.
 * It resolves the provided `color` against the theme first (when a known
 * `ThemeColorName` is provided) and falls back to any valid CSS color string.
 *
 * Usage examples:
 *
 * ```tsx
 * <OutlineBadge color="primary">Primary</OutlineBadge>
 * <OutlineBadge color="#ff6600" small>Custom</OutlineBadge>
 * ```
 */
const OutlineBadge: React.FC<OutlineBadgeProps> = ({
	color,
	small,
	children,
	...rest
}) => {
	const theme = useTheme() as Theme

	/**
	 * Resolve the final color for the badge.
	 *
	 * - If no color is provided, use the theme's `primary` color.
	 * - If a color is provided and it matches a key in the theme, prefer the themed value.
	 * - Otherwise, treat the provided value as a raw CSS color string.
	 */
	const resolveColor = (): string => {
		if (!color) return theme.primary
		const candidate = String(color)
		// Prefer themed color values when the candidate matches a known theme color name
		if ((candidate as ThemeColorName) in theme) {
			const key = candidate as ThemeColorName
			return theme[key]
		}
		return candidate
	}

	const resolvedColor = resolveColor()

	return (
		<Badge $color={resolvedColor} $small={small} {...rest}>
			{children}
		</Badge>
	)
}

export default OutlineBadge
